/**
 * <s-announcement-slider> — Rotating announcement messages
 * Slides through its [data-slide] children one at a time. Pauses while hovered
 * or focused, and stays on the first message when reduced motion is preferred.
 *
 * Attributes:
 *   interval — seconds each message stays visible (default 4)
 */
class SAnnouncementSlider extends HTMLElement {
  #slides = [];
  #index = 0;
  #timer;
  #paused = false;
  #ac;

  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-slide]')];
    if (this.#slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('mouseenter', () => (this.#paused = true), opts);
    this.addEventListener('mouseleave', () => (this.#paused = false), opts);
    this.addEventListener('focusin', () => (this.#paused = true), opts);
    this.addEventListener('focusout', () => (this.#paused = false), opts);

    const interval = Number(this.getAttribute('interval') || 4) * 1000;
    this.#timer = setInterval(() => {
      if (!this.#paused && !document.hidden) this.#next();
    }, interval);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#ac?.abort();
  }

  #next() {
    const current = this.#slides[this.#index];
    this.#index = (this.#index + 1) % this.#slides.length;
    const next = this.#slides[this.#index];

    current.dataset.state = 'out';
    current.setAttribute('aria-hidden', 'true');
    next.dataset.state = 'in';
    next.removeAttribute('aria-hidden');

    setTimeout(() => {
      if (current.dataset.state === 'out') current.dataset.state = 'off';
    }, 500);
  }
}

customElements.define('s-announcement-slider', SAnnouncementSlider);
